import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConfirmDialog } from '../../../components/confirm-dialog';
import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import {
  type AccountDeletionBlocker,
  apiErrorCode,
  deleteClientAccount,
  deletionBlockers,
} from '../api';
import { type User } from '../types';

type Failure = { kind: 'blocked'; reasons: AccountDeletionBlocker[] } | { kind: 'message'; key: string };

const MESSAGE_CODES = new Set(['ADMIN_ACCOUNT', 'ACCOUNT_ALREADY_DELETED', 'USER_NOT_FOUND']);

/**
 * «Видалити акаунт» on the «Клієнт» header (legal-pages phase 4) — the operator
 * executes a client's emailed deletion request through
 * `DELETE /admin/users/:id/account`.
 *
 * The confirmation names the client's email, so the operator checks it against
 * the request. A 409 ACCOUNT_DELETION_BLOCKED keeps the dialog open and lists
 * every blocker the API returned; success toasts and hands control back
 * (`onDeleted` — the page returns to the list).
 */
export function DeleteClientAccountAction({
  user,
  onDeleted,
}: {
  user: User;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const mutation = useMutation({
    mutationFn: () => deleteClientAccount(user._id as string),
    onSuccess: () => {
      toast.success(t('clients.deleteAccount.success'));
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.removeQueries({ queryKey: ['user', user._id] });
      onDeleted();
    },
    onError: (error) => {
      const code = apiErrorCode(error);
      if (code === 'ACCOUNT_DELETION_BLOCKED') {
        setFailure({ kind: 'blocked', reasons: deletionBlockers(error) });
      } else if (code && MESSAGE_CODES.has(code)) {
        setFailure({ kind: 'message', key: `clients.deleteAccount.errors.${code}` });
      } else {
        setFailure({ kind: 'message', key: 'clients.deleteAccount.errors.generic' });
      }
    },
  });

  if (!user._id) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setFailure(null);
          setOpen(true);
        }}
        disabled={mutation.isPending}
        className="flex h-10 flex-1 items-center justify-center rounded-cf-md border border-cf-danger/35 bg-cf-surface px-3.5 text-[13px] font-semibold text-cf-danger hover:bg-cf-danger-tint disabled:opacity-50 lg:flex-none lg:text-sm"
      >
        {t('clients.deleteAccount.action')}
      </button>

      <ConfirmDialog
        open={open}
        tone="danger"
        title={t('clients.deleteAccount.title')}
        message={
          <>
            <p>{t('clients.deleteAccount.message', { email: user.email })}</p>
            {failure && (
              <div
                role="alert"
                className="mt-3 rounded-cf-md border border-cf-danger/35 bg-cf-danger-tint p-3 text-[13px] text-cf-danger"
              >
                {failure.kind === 'blocked' ? (
                  <>
                    <p className="font-semibold">{t('clients.deleteAccount.blocked.title')}</p>
                    <ul className="mt-1.5 list-disc pl-5">
                      {failure.reasons.map((reason) => (
                        <li key={reason}>{t(`clients.deleteAccount.blocked.${reason}`)}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  t(failure.key)
                )}
              </div>
            )}
          </>
        }
        confirmLabel={
          mutation.isPending ? t('common.loading.default') : t('clients.deleteAccount.confirm')
        }
        busy={mutation.isPending}
        onConfirm={() => mutation.mutate()}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}
